import React, { useState } from 'react';

function Contact({ onBack }) {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    company: '',
    message: '',
    honeypot: ''
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [formStartTime] = useState(Date.now());

  // Load reCAPTCHA v3 script
  React.useEffect(() => {
    if (!document.querySelector('#recaptcha-v3-script')) {
      const script = document.createElement('script');
      script.id = 'recaptcha-v3-script';
      script.src = `https://www.google.com/recaptcha/api.js?render=${process.env.REACT_APP_RECAPTCHA_SITE_KEY}`;
      script.async = true;
      document.head.appendChild(script);
    }
  }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Honeypot check
    if (formData.honeypot) return;
    
    // Basic validation
    if (!formData.name || !formData.email || !formData.message) return;
    
    // Time-based validation (minimum 5 seconds)
    if (Date.now() - formStartTime < 5000) {
      alert('Please take your time to fill the form');
      setIsSubmitting(false);
      return;
    }
    
    setIsSubmitting(true);

    // Ambil token reCAPTCHA v3 saat submit
    let recaptchaToken = '';
    try {
      recaptchaToken = await window.grecaptcha.execute(
        process.env.REACT_APP_RECAPTCHA_SITE_KEY,
        { action: 'contact' }
      );
    } catch {
      alert('reCAPTCHA gagal dimuat. Coba refresh halaman.');
      setIsSubmitting(false);
      return;
    }
    
    try {
      const response = await fetch(`${process.env.REACT_APP_API_URL}/api/contact`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          ...formData,
          recaptchaToken,
          timestamp: Date.now(),
          userAgent: navigator.userAgent
        }),
      });

      const result = await response.json();

      if (response.ok) {
        setSubmitted(true);
      } else {
        alert(`Error: ${result.message}`);
      }
    } catch (error) {
      console.error('Error:', error);
      alert('Failed to connect to server');
    }
    
    setIsSubmitting(false);
  };

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  if (submitted) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="max-w-md mx-auto text-center">
          <div className="bg-green-100 rounded-full w-16 h-16 flex items-center justify-center mx-auto mb-4">
            <svg className="w-8 h-8 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
            </svg>
          </div>
          <h2 className="text-2xl font-bold mb-2">Message Sent!</h2>
          <p className="text-gray-600 mb-6">We'll get back to you within 24 hours.</p>
          <button 
            onClick={onBack} 
            className="bg-black text-white px-6 py-3 rounded-full hover:bg-gray-800 transition"
          >
            Back to Home
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <header className="bg-white border-b border-gray-100">
        <nav className="container mx-auto px-6 py-4 flex justify-between items-center">
          <img src="/x-logo.svg" alt="xharely" className="h-10 w-10" />
          <button 
            onClick={onBack}
            className="text-gray-700 hover:text-black transition font-medium"
          >
            ← Back
          </button>
        </nav>
      </header>

      <section className="py-20">
        <div className="container mx-auto px-6">
          <div className="max-w-2xl mx-auto">
            <div className="text-center mb-12">
              <h1 className="text-4xl font-bold mb-4">Let's Get Coffee ☕</h1>
              <p className="text-gray-600 text-lg">
                Ready to discuss how xharely can transform your business? Drop us a message.
              </p>
            </div>

            <form onSubmit={handleSubmit} className="bg-white rounded-2xl p-8 shadow-sm border border-gray-100">
              {/* Honeypot field - hidden from users */}
              <input
                type="text"
                name="honeypot"
                value={formData.honeypot}
                onChange={handleChange}
                style={{ display: 'none' }}
                tabIndex="-1"
                autoComplete="off"
              />

              <div className="grid md:grid-cols-2 gap-6 mb-6">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Name *
                  </label>
                  <input
                    type="text"
                    name="name"
                    value={formData.name}
                    onChange={handleChange}
                    required
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-yellow-400 focus:border-transparent outline-none transition"
                    placeholder="Your full name"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Email *
                  </label>
                  <input
                    type="email"
                    name="email"
                    value={formData.email}
                    onChange={handleChange}
                    required
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-yellow-400 focus:border-transparent outline-none transition"
                    placeholder="your@email.com"
                  />
                </div>
              </div>

              <div className="mb-6">
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Company
                </label>
                <input
                  type="text"
                  name="company"
                  value={formData.company}
                  onChange={handleChange}
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-yellow-400 focus:border-transparent outline-none transition"
                  placeholder="Your company name"
                />
              </div>

              <div className="mb-6">
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Message *
                </label>
                <textarea
                  name="message"
                  value={formData.message}
                  onChange={handleChange}
                  required
                  rows="5"
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-yellow-400 focus:border-transparent outline-none transition resize-none"
                  placeholder="Tell us about your project or business needs..."
                />
              </div>

              <button
                type="submit"
                disabled={isSubmitting}
                className="w-full bg-black text-white py-4 rounded-lg font-medium hover:bg-gray-800 transition disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {isSubmitting ? 'Sending...' : 'Send Message'}
              </button>

              <p className="text-xs text-gray-400 text-center mt-4 leading-relaxed">
                This site is protected by reCAPTCHA and the Google{' '}
                <a href="https://policies.google.com/privacy" target="_blank" rel="noopener noreferrer" className="underline hover:text-gray-600">Privacy Policy</a>
                {' '}and{' '}
                <a href="https://policies.google.com/terms" target="_blank" rel="noopener noreferrer" className="underline hover:text-gray-600">Terms of Service</a>
                {' '}apply.
              </p>

              <p className="text-sm text-gray-500 text-center mt-3">
                We typically respond within 24 hours during business days.
              </p>
            </form>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-white pt-16 pb-12 border-t border-gray-100 mt-12">
        <div className="container mx-auto px-6">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-10 pb-12 border-b border-gray-100">
            {/* Brand Column */}
            <div className="space-y-4">
              <img src="/x-logo-text.svg" alt="xharely - menghubungkan ide membangun solusi digital" className="h-8 md:h-10 w-auto" />
              <p className="text-gray-600 text-sm leading-relaxed max-w-sm">
                Menghubungkan ide inovatif dengan solusi digital terbaik untuk mempercepat pertumbuhan dan efisiensi bisnis Anda.
              </p>
            </div>

            {/* Office Address Column */}
            <div className="space-y-3">
              <h3 className="text-sm font-bold uppercase tracking-wider text-black">Alamat Kantor</h3>
              <p className="text-gray-600 text-sm leading-relaxed">
                Jl. Kayangan, Tangkerang Barat, Marpoyan Damai, Pekanbaru, Riau, Indonesia (28282)
              </p>
            </div>

            {/* Contact Column */}
            <div className="space-y-3">
              <h3 className="text-sm font-bold uppercase tracking-wider text-black">Hubungi Kami</h3>
              <ul className="space-y-2.5 text-sm text-gray-600">
                <li>
                  <a 
                    href="https://wa.me/6285121210306?text=Halo%20xharely%2C%20saya%20tertarik%20untuk%20konsultasi%20layanan%20pengembangan%20software%20%2F%20solusi%20digital." 
                    target="_blank" 
                    rel="noopener noreferrer" 
                    className="inline-flex items-center gap-2.5 text-gray-700 hover:text-green-600 transition font-medium"
                  >
                    <svg className="w-5 h-5 text-green-500 fill-current flex-shrink-0" viewBox="0 0 24 24">
                      <path d="M.057 24l1.687-6.163c-1.041-1.804-1.588-3.849-1.587-5.946.003-6.556 5.338-11.891 11.893-11.891 3.181.001 6.167 1.24 8.413 3.488 2.245 2.248 3.481 5.236 3.48 8.414-.003 6.557-5.338 11.892-11.893 11.892-1.99-.001-3.951-.5-5.688-1.448l-6.305 1.654zm6.597-3.807c1.676.995 3.276 1.591 5.392 1.592 5.448 0 9.886-4.434 9.889-9.885.002-5.462-4.415-9.89-9.881-9.892-5.452 0-9.887 4.434-9.889 9.884-.001 2.225.651 3.891 1.746 5.634l-.999 3.648 3.742-.981zm11.387-5.464c-.074-.124-.272-.198-.57-.347-.297-.149-1.758-.868-2.031-.967-.272-.099-.47-.149-.669.149-.198.297-.768.967-.941 1.165-.173.198-.347.223-.644.074-.297-.149-1.255-.462-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.297-.347.446-.521.151-.172.2-.296.3-.495.099-.198.05-.372-.025-.521-.075-.148-.669-1.611-.916-2.206-.242-.579-.487-.501-.669-.51l-.57-.01c-.198 0-.52.074-.792.372s-1.04 1.016-1.04 2.479 1.065 2.876 1.213 3.074c.149.198 2.095 3.2 5.076 4.487.709.306 1.263.489 1.694.626.712.226 1.36.194 1.872.118.571-.085 1.758-.719 2.006-1.413.248-.695.248-1.29.173-1.414z"/>
                    </svg>
                    <span>WhatsApp: +62 851-2121-0306</span>
                  </a>
                </li>
                <li>
                  <a 
                    href="mailto:contact@xharely.com" 
                    className="inline-flex items-center gap-2.5 text-gray-700 hover:text-black transition font-medium"
                  >
                    <svg className="w-5 h-5 text-gray-500 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
                    </svg>
                    <span>Email: contact@xharely.com</span>
                  </a>
                </li>
              </ul>
            </div>
          </div>

          <div className="pt-8 text-center md:text-left text-sm text-gray-500">
            <p>&copy; {new Date().getFullYear()} PT Xharely Teknologi Indonesia. All rights reserved.</p>
          </div>
        </div>
      </footer>
    </div>
  );
}

export default Contact;